import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bell, MessageCircleQuestion, Pencil, Plus, StickyNote, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useFetch } from "@/hooks/useFetch";
import { spaceOf } from "@/lib/spaces";
import { fmtDay, projectProgress } from "@/lib/time";
import { Empty, Loading, Panel } from "@/components/Bits";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { StatusPicker } from "@/components/StatusPicker";
import { DaysLeft, ProgressBar } from "@/components/ProjectCard";
import { ReminderItem } from "@/components/ReminderItem";
import { NoteCard } from "@/components/NoteCard";

const AddLink = ({ onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">
    <Plus className="h-3.5 w-3.5" /> {label}
  </button>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { run, openAdd, openEdit } = useApp();
  const p = useFetch(`/projects/${id}`);

  if (p === null) return <Loading />;
  if (p === false) return <Empty text="Project not found." testid="project-not-found" action={<Link to="/projects" className="text-sm underline">Back to projects</Link>} />;

  const s = spaceOf(p.space);
  const pct = projectProgress(p);
  const defaults = { project_id: p.id, space: p.space };
  const notes = p.notes.filter((n) => n.kind === "note");
  const thoughts = p.notes.filter((n) => n.kind === "thought");
  const setStatus = (status) => run(() => api.patch(`/projects/${p.id}`, { status }), "Status updated");
  const remove = async () => (await run(() => api.delete(`/projects/${p.id}`), "Project deleted")) && nav("/projects");

  return (
    <div data-testid={`project-detail-${p.id}`}>
      <Link to="/projects" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-projects"><ArrowLeft className="h-4 w-4" /> Projects</Link>
      <div className="fade-up rounded-2xl border bg-card/70 p-6 sm:p-8" style={{ boxShadow: `inset 0 3px 0 ${s.color}` }}>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><SpaceAvatar space={p.space} /> {s.name}'s project</div>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="project-title">{p.title}</h1>
            {p.description && <p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">{p.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openEdit("project", p)} data-testid="project-edit-button" className="flex h-9 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="project-delete-button" className="flex h-9 w-9 items-center justify-center rounded-full border text-red-400 transition-colors hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                  <AlertDialogDescription>Its reminders and notes stay, they just won't be linked to a project any more.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} data-testid="project-delete-confirm" className="bg-red-500 text-white hover:bg-red-600">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <div className="mt-8 grid gap-6 border-t pt-6 md:grid-cols-[auto_1fr] md:items-center">
          <StatusPicker value={p.status} onChange={setStatus} testPrefix="project-status" />
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="font-mono">{fmtDay(p.start_date) || "No start"} → {fmtDay(p.end_date) || "No due date"}</span>
              <DaysLeft p={p} />
              {pct != null && <span className="ml-auto font-mono">{pct}%</span>}
            </div>
            {pct != null && <ProgressBar value={pct} color={s.color} />}
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Reminders" count={p.reminders.length} testid="project-reminders" action={<AddLink label="Reminder" onClick={() => openAdd("reminder", defaults)} testid="project-add-reminder" />}>
          {p.reminders.length === 0 ? <Empty icon={Bell} text="No reminders in this project yet." /> : (
            <div className="space-y-3">{p.reminders.map((r) => <ReminderItem key={r.id} r={r} showProject={false} />)}</div>
          )}
        </Panel>
        <div className="space-y-6">
          <Panel title="Notes" count={notes.length} testid="project-notes" action={<AddLink label="Note" onClick={() => openAdd("note", defaults)} testid="project-add-note" />}>
            {notes.length === 0 ? <Empty icon={StickyNote} text="No notes yet." /> : notes.map((n) => <NoteCard key={n.id} n={n} showProject={false} />)}
          </Panel>
          <Panel title="To discuss" count={thoughts.length} testid="project-thoughts" action={<AddLink label="Thought" onClick={() => openAdd("thought", defaults)} testid="project-add-thought" />}>
            {thoughts.length === 0 ? <Empty icon={MessageCircleQuestion} text="Nothing to discuss on this one." /> : thoughts.map((n) => <NoteCard key={n.id} n={n} showProject={false} />)}
          </Panel>
        </div>
      </div>
    </div>
  );
}
